/**
 * adminData — cached loaders for the heaviest / most-shared admin reads, built on
 * adminCache (which wraps the in-memory cacheService). Import these instead of
 * calling getDocs directly so every page shares one cache entry per collection.
 *
 * - loadRevenueCached: the ALL-orders + ALL-games bundle (RevenueAnalytics +
 *   AdminOverview share it — the single heaviest read in the app).
 * - loadGamesCached: the `games` collection, read independently by ~11 pages;
 *   one 20-min entry serves them all. Invalidate with invalidate(cacheKeys.games)
 *   after a game create/edit/score save.
 *
 * Pass force=true (e.g. an explicit Refresh) to bypass and refresh the cache.
 */
import { collection, getDocs, getDoc, setDoc, doc, increment, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firestore';
import { loadRevenueData, type LoadedRevenueData } from '../utils/revenueAnalytics';
import { cachedRead, invalidate, cacheKeys, CACHE_TTL } from './adminCache';

/** All orders + games, cached (business rollup TTL). Shared by the revenue pages. */
export function loadRevenueCached(force = false): Promise<LoadedRevenueData> {
  return cachedRead(cacheKeys.revenueData, loadRevenueData, CACHE_TTL.BUSINESS, force);
}

/** Server-computed revenue summary (revenueMeta/summary) — headline totals +
 * 12-month series + seasons list, so the dashboard doesn't scan every order.
 * Returns null if the rollup hasn't run yet (callers fall back to a live load). */
export interface RevenueSummary {
  totalRevenue: number;
  ticketRevenue: number;
  merchRevenue: number;
  orderCount: number;
  ticketUnits: number;
  merchUnits: number;
  monthly: Array<{ label: string; value: number }>;
  seasonsWithSales: number[];
  updatedAt?: unknown;
}
export function loadRevenueSummary(force = false): Promise<RevenueSummary | null> {
  return cachedRead(
    cacheKeys.custom('revenueSummary'),
    async () => {
      const snap = await getDoc(doc(db, 'revenueMeta', 'summary'));
      return snap.exists() ? (snap.data() as RevenueSummary) : null;
    },
    CACHE_TTL.BUSINESS,
    force
  );
}

/**
 * Force the server to recompute the revenue rollup (revenueMeta/summary) NOW,
 * instead of waiting for the 30-min scheduler, and drop the client cache so the
 * next loadRevenueSummary reads the fresh figures. Returns the scanned order count.
 */
export async function refreshRevenueRollup(): Promise<number> {
  const call = httpsCallable<void, { success: boolean; orderCountScanned: number }>(functions, 'refreshRevenueRollup');
  const res = await call();
  invalidate(cacheKeys.custom('revenueSummary'));
  return res.data?.orderCountScanned ?? 0;
}

// --- Games: version-sentinel caching --------------------------------------
//
// The schedule changes maybe a few times a season, so re-reading the whole
// `games` collection on a short TTL wastes reads. Instead we cache the full
// list "forever" (24 h cap) keyed by a VERSION, and only do a cheap 1-doc
// version check (cached ~5 min) to decide whether to re-fetch. When any game is
// created/edited/scored/deleted, callers invoke `bumpGamesVersion()`, which
// increments the version so EVERY session (public site included) picks up the
// change on its next read — no stale schedule, and no repeated full reads.
//
// The sentinel lives at games/__meta__ so it's publicly readable (games are)
// and writable by anyone with the `games` permission (the game editors).
const GAMES_META_ID = '__meta__';

async function fetchGamesRev(): Promise<number> {
  try {
    const snap = await getDoc(doc(db, 'games', GAMES_META_ID));
    return snap.exists() ? Number((snap.data() as { rev?: number }).rev || 0) : 0;
  } catch {
    return 0; // sentinel unreadable → fall back to a single stable version
  }
}

/** The `games` collection as `{ id, ...data }[]` (excluding the meta doc), cached
 * per version. Callers cast to their own Game shape. */
export async function loadGamesCached<T = Record<string, unknown>>(
  force = false
): Promise<Array<{ id: string } & T>> {
  const rev = await cachedRead(cacheKeys.custom('gamesRev'), fetchGamesRev, CACHE_TTL.VOLATILE, force);
  return cachedRead(
    cacheKeys.custom('games', `v${rev}`),
    async () => {
      const snap = await getDocs(collection(db, 'games'));
      return snap.docs
        .filter((d) => d.id !== GAMES_META_ID)
        .map((d) => ({ id: d.id, ...(d.data() as T) }));
    },
    CACHE_TTL.STATIC,
    force
  );
}

/** Bump the games version after any game create/edit/score/delete so every
 * session re-fetches the schedule on its next read. Also drops THIS session's
 * version cache so the editor sees their own change immediately. Best-effort:
 * a failed sentinel write never breaks the underlying game save. */
export async function bumpGamesVersion(): Promise<void> {
  try {
    await setDoc(
      doc(db, 'games', GAMES_META_ID),
      { rev: increment(1), updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (err) {
    console.warn('bumpGamesVersion: sentinel write failed (game still saved)', err);
  }
  invalidate(cacheKeys.custom('gamesRev'));
}

// --- Public-site reference reads ------------------------------------------
//
// The public site (splash, sponsor, team, shop, alumni, footer) re-reads whole
// reference collections on every visit. These loaders cache each collection once
// per customer session under a PUBLIC key — deliberately distinct from the admin
// managers' keys so a customer session can never collide with (or depend on) the
// admin cache's projected shapes/order. Every consumer filters/sorts client-side,
// so the cached array is the raw collection (no where/orderBy → no composite index
// needed either). REFERENCE TTL (20 min) bounds staleness; a hard refresh re-reads.

/** Spread shape `{ id, ...data }` — matches how the public pages already map docs. */
async function loadPublicSpread<T = Record<string, unknown>>(
  key: string,
  coll: string,
  force = false
): Promise<Array<{ id: string } & T>> {
  return cachedRead(
    key,
    async () => {
      const snap = await getDocs(collection(db, coll));
      // id LAST so the real doc id always wins, even if a doc body carries an `id` field.
      return snap.docs.map((d) => ({ ...(d.data() as T), id: d.id }));
    },
    CACHE_TTL.REFERENCE,
    force
  );
}

export const loadPublicSponsors = <T = Record<string, unknown>>(force = false) =>
  loadPublicSpread<T>(cacheKeys.custom('publicSponsors'), 'sponsors', force);
export const loadPublicPlayers = <T = Record<string, unknown>>(force = false) =>
  loadPublicSpread<T>(cacheKeys.custom('publicPlayers'), 'players', force);
export const loadPublicStaff = <T = Record<string, unknown>>(force = false) =>
  loadPublicSpread<T>(cacheKeys.custom('publicStaff'), 'staff', force);
export const loadPublicMerchandise = <T = Record<string, unknown>>(force = false) =>
  loadPublicSpread<T>(cacheKeys.custom('publicMerchandise'), 'merchandise', force);

/** Nested shape `{ id, data }` (RawDoc) — matches PressReleaseDetail's cache so the
 * homepage / footer / detail pages share ONE entry per collection. */
export type PublicRawDoc = { id: string; data: Record<string, unknown> };
async function loadPublicRaw(key: string, coll: string, force = false): Promise<PublicRawDoc[]> {
  return cachedRead<PublicRawDoc[]>(
    key,
    async () => {
      const snap = await getDocs(collection(db, coll));
      return snap.docs.map((d) => ({ id: d.id, data: d.data() as Record<string, unknown> }));
    },
    CACHE_TTL.REFERENCE,
    force
  );
}
export const loadPublicPressReleases = (force = false) =>
  loadPublicRaw(cacheKeys.custom('publicPressReleases'), 'pressReleases', force);
export const loadPublicLatestNews = (force = false) =>
  loadPublicRaw(cacheKeys.custom('publicLatestNews'), 'latestNews', force);
